'use client';

import { AnswerTemplateVariableHint } from '@/components/answer-templates/AnswerTemplateVariableHint';
import { FC } from 'react';
import { UIPriority } from '@/enums/ui-priorities';
import { formatDateTimeInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import { formatReviewTableTextForDisplay } from '@/shared/format-review-table-text-for-display';
import Button from '@/ui/Button';
import Modal from '@/ui/Modal';
import Rating from '@/ui/Rating';
import Textarea from '@/ui/Textarea';
import { ModalAnswerPickedProps } from '@/components/modal-answer-picked/modal-answer-picked.types';
import { ModalAnswerPickedControllerBindings } from '@/components/modal-answer-picked/use-modal-answer-picked-controller';
import { AnswerTemplatesControl } from '@/components/answer-templates/AnswerTemplatesControl';
import { QuotaActionButton } from '@/components/billing/QuotaTopUpButton';
import { DialogActionGrid } from '@/components/dialog-actions/DialogActionGrid';
import { getSelectedAutoRepliesRunLabels } from '@/shared/auto-replies-labels';

type ModalAnswerPickedViewProps = ModalAnswerPickedProps & ModalAnswerPickedControllerBindings;

export const ModalAnswerPickedView: FC<ModalAnswerPickedViewProps> = ({
    isShown,
    setIsShown,
    value,
    setValue,
    listRef,
    isSelectedReviewsLoading,
    selectedReviewsErrorText,
    selectedReviewsForAnswer,
    isAiGenerating,
    aiErrorText,
    virtualizedReviews,
    virtualizedRangeStart,
    virtualizedTotalHeight,
    virtualRowHeight,
    canSubmit,
    canGenerateAiAnswer,
    aiAnswerDisabledReason,
    isAiAnswerQuotaExhausted,
    onVirtualListScroll,
    onGenerateAiAnswer,
    onAnswerReviews,
    onOpenSelectedAutoReplies,
    onPhotoPreviewOpen,
    onPhotoPreviewClose,
    onPreviousPhoto,
    onNextPhoto,
    openedPhotos,
    openedPhoto,
    isPhotoLoading,
    onPhotoPreviewImageLoad,
    onPhotoPreviewImageError,
    answerTemplates,
    isTemplatesLoading,
    templatesErrorText,
    isTemplatesMenuOpen,
    isTemplatesEditorShown,
    editingTemplateId,
    templateNameDraft,
    templateTextDraft,
    templateFormErrorText,
    isTemplateSaving,
    isTemplateDeleting,
    canSaveTemplate,
    canDeleteTemplate,
    onToggleTemplatesMenu,
    onCloseTemplatesMenu,
    onOpenTemplatesEditor,
    onCloseTemplatesEditor,
    onApplyTemplate,
    onSelectTemplateForEdit,
    onStartNewTemplateDraft,
    onTemplateNameDraftChange,
    onTemplateTextDraftChange,
    onSaveTemplate,
    onDeleteTemplate,
}) => {
    const selectedReviewsCount = selectedReviewsForAnswer.length;
    const modalTitle = selectedReviewsCount >= 2 ? `Диалог (${selectedReviewsCount})` : 'Диалог';
    const selectedAutoRepliesLabels = getSelectedAutoRepliesRunLabels('ru');

    return (
        <Modal isShown={isShown} setIsShown={setIsShown} title={modalTitle} closable>
            <div className="p-2 w-full h-full flex flex-col gap-2">
                <div
                    ref={listRef}
                    className="w-full grow min-h-0 overflow-auto"
                    onScroll={(event) => onVirtualListScroll(event.currentTarget.scrollTop)}
                >
                    {isSelectedReviewsLoading ? (
                        <div className="h-full w-full flex items-center justify-center text-sm text-gray-500">
                            Загрузка отзывов...
                        </div>
                    ) : selectedReviewsErrorText ? (
                        <div className="h-full w-full flex items-center justify-center text-sm text-red-500">
                            {selectedReviewsErrorText}
                        </div>
                    ) : !selectedReviewsForAnswer.length ? (
                        <div className="h-full w-full flex items-center justify-center text-sm text-gray-500">
                            Нет отзывов без ответа среди выбранных
                        </div>
                    ) : (
                        <div className="relative" style={{ height: virtualizedTotalHeight }}>
                            {virtualizedReviews.map((elem, virtualIndex) => {
                                const absoluteIndex = virtualizedRangeStart + virtualIndex;
                                const preparedReview = formatReviewTableTextForDisplay(elem, {
                                    prosConsOnNewLines: true,
                                });
                                const reviewPhotos = elem.photos || [];
                                const reviewCreatedDateLabel =
                                    formatDateTimeInGmtPlus3(elem.createdDate) || elem.createdDate;
                                const reviewRating = Number(elem.rating) || 0;

                                return (
                                    <div
                                        key={elem.id}
                                        className="absolute left-0 right-0 px-0.5 pb-2"
                                        style={{ top: absoluteIndex * virtualRowHeight, height: virtualRowHeight }}
                                    >
                                        <div className="p-1.5 rounded-lg bg-black/6 h-full flex flex-col gap-1 text-sm leading-5">
                                            <div className="flex items-center gap-1">
                                                <Rating value={reviewRating} />
                                                <span className="text-xs text-slate-600">{`${reviewRating}/5`}</span>
                                            </div>
                                            <p className="line-clamp-4 whitespace-pre-line break-words">
                                                {preparedReview || '[Отзыв без текста]'}
                                            </p>

                                            {reviewPhotos.length > 0 && (
                                                <div className="flex items-center gap-1 overflow-x-auto">
                                                    {reviewPhotos.map((photo, photoIndex) => (
                                                        <button
                                                            type="button"
                                                            key={`${elem.id}-photo-${photoIndex}`}
                                                            aria-label={`Открыть фото ${photoIndex + 1}`}
                                                            className="h-[30px] w-[30px] shrink-0 overflow-hidden rounded border border-slate-200 transition-colors hover:border-slate-500"
                                                            onClick={() => onPhotoPreviewOpen(reviewPhotos, photoIndex)}
                                                        >
                                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                                            <img
                                                                src={photo.miniSize}
                                                                alt={`Фото ${photoIndex + 1}`}
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {reviewCreatedDateLabel && (
                                                <p className="text-gray-500 text-xs mt-auto flex justify-end">
                                                    {reviewCreatedDateLabel}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <Textarea
                    value={value}
                    className="border border-gray-300 h-1/4 min-h-[126px]"
                    setValue={setValue}
                    placeholder={'Нажмите "ИИ-ответ" или введите свой текст сообщения'}
                />
                <AnswerTemplateVariableHint padded />
                {aiErrorText && <p className="text-red-600 text-xs px-1">{aiErrorText}</p>}
                <DialogActionGrid
                    templatesAction={
                        <AnswerTemplatesControl
                            answerTemplates={answerTemplates}
                            isTemplatesLoading={isTemplatesLoading}
                            templatesErrorText={templatesErrorText}
                            isTemplatesMenuOpen={isTemplatesMenuOpen}
                            isTemplatesEditorShown={isTemplatesEditorShown}
                            editingTemplateId={editingTemplateId}
                            templateNameDraft={templateNameDraft}
                            templateTextDraft={templateTextDraft}
                            templateFormErrorText={templateFormErrorText}
                            isTemplateSaving={isTemplateSaving}
                            isTemplateDeleting={isTemplateDeleting}
                            canSaveTemplate={canSaveTemplate}
                            canDeleteTemplate={canDeleteTemplate}
                            onToggleTemplatesMenu={onToggleTemplatesMenu}
                            onCloseTemplatesMenu={onCloseTemplatesMenu}
                            onOpenTemplatesEditor={onOpenTemplatesEditor}
                            onCloseTemplatesEditor={onCloseTemplatesEditor}
                            onApplyTemplate={onApplyTemplate}
                            onSelectTemplateForEdit={onSelectTemplateForEdit}
                            onStartNewTemplateDraft={onStartNewTemplateDraft}
                            onTemplateNameDraftChange={onTemplateNameDraftChange}
                            onTemplateTextDraftChange={onTemplateTextDraftChange}
                            onSaveTemplate={onSaveTemplate}
                            onDeleteTemplate={onDeleteTemplate}
                            buttonClassName="h-full w-full"
                        />
                    }
                    aiAction={
                        <QuotaActionButton
                            reason={aiAnswerDisabledReason}
                            isQuotaExhausted={isAiAnswerQuotaExhausted}
                            priorityType={UIPriority.SECONDARY}
                            uiSize="compact"
                            onClick={onGenerateAiAnswer}
                            disabled={!canGenerateAiAnswer}
                            className="h-full w-full"
                            tooltipClassName="h-full w-full"
                        >
                            {isAiGenerating ? 'Генерируем...' : 'ИИ-ответ'}
                        </QuotaActionButton>
                    }
                    autoRepliesAction={
                        <Button
                            type="button"
                            priorityType={UIPriority.SECONDARY}
                            uiSize="compact"
                            onClick={onOpenSelectedAutoReplies}
                            className="h-full w-full"
                        >
                            {selectedAutoRepliesLabels.action}
                        </Button>
                    }
                    primaryAction={
                        <Button
                            priorityType={UIPriority.PRIMARY}
                            uiSize="compact"
                            onClick={onAnswerReviews}
                            disabled={!canSubmit}
                            className="flex h-full w-full items-center justify-center whitespace-normal px-2 text-center leading-tight"
                        >
                            Ответить
                        </Button>
                    }
                />
            </div>
            {openedPhotos && (
                <Modal
                    isShown={true}
                    setIsShown={onPhotoPreviewClose}
                    hideHeader
                    className="h-[90vh] w-[90vw] max-w-[1000px]"
                >
                    <div className="relative h-full w-full bg-slate-900 p-4">
                        <button
                            type="button"
                            aria-label="Закрыть просмотр фото"
                            className="absolute right-4 top-4 z-10 h-8 w-8 rounded-full bg-white/90 text-sm text-slate-900 transition-colors hover:bg-white"
                            onClick={onPhotoPreviewClose}
                        >
                            x
                        </button>
                        <div className="flex h-full items-center justify-center gap-4">
                            <button
                                type="button"
                                aria-label="Предыдущее фото"
                                className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                onClick={onPreviousPhoto}
                                disabled={openedPhotos.photos.length < 2}
                            >
                                {'<'}
                            </button>
                            <div className="relative flex h-full w-full items-center justify-center">
                                {isPhotoLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded bg-slate-800/70 text-sm text-slate-200">
                                        Загрузка фото...
                                    </div>
                                )}
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    key={`${openedPhotos.index}-${openedPhoto?.fullSize || ''}`}
                                    src={openedPhoto?.fullSize || ''}
                                    alt={`Фото ${openedPhotos.index + 1}`}
                                    className={`max-h-full max-w-full object-contain transition-opacity duration-150 ${
                                        isPhotoLoading ? 'opacity-0' : 'opacity-100'
                                    }`}
                                    onLoad={onPhotoPreviewImageLoad}
                                    onError={onPhotoPreviewImageError}
                                />
                            </div>
                            <button
                                type="button"
                                aria-label="Следующее фото"
                                className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                onClick={onNextPhoto}
                                disabled={openedPhotos.photos.length < 2}
                            >
                                {'>'}
                            </button>
                        </div>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-black/70 px-3 py-1 text-sm text-white">
                            {openedPhotos.index + 1} / {openedPhotos.photos.length}
                        </div>
                    </div>
                </Modal>
            )}
        </Modal>
    );
};
